import { useEffect, useState } from 'react'
import { api, type AiAccountSpend } from '../lib/api.ts'
import {
  hasUnattributed,
  NO_ACCOUNT,
  spendByDay,
  type AccountSpendDay,
} from '../lib/accountSpend.ts'
import { DataTable, type Column } from './DataTable.tsx'

const money = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' })

/**
 * What each pharmacy's model calls cost, day by day (#640).
 *
 * Fetched on its own, after the rest of the screen, because the source asks
 * Langfuse once per account; the seats above must not wait for it.
 */
export function AccountSpend({ days }: { days: number }) {
  const [data, setData] = useState<AiAccountSpend | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(true)

  function load() {
    setLoading(true)
    void api
      .aiAccountSpend(days)
      .then((r) => {
        setData(r)
        setError(undefined)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(load, [days])

  return <AccountSpendTable data={data} error={error} loading={loading} onRetry={load} />
}

/** The table alone, so it renders from data in a test. */
export function AccountSpendTable({
  data,
  error,
  loading,
  onRetry,
}: {
  data: AiAccountSpend | undefined
  error?: string | undefined
  loading: boolean
  onRetry?: () => void
}) {
  const rows = data?.spend ?? []
  const columns: Array<Column<AccountSpendDay>> = [
    {
      key: 'day',
      header: 'Dia (UTC)',
      className: 'tabular-nums',
      render: (row) => row.day.split('-').reverse().join('/'),
    },
    ...(data?.accounts ?? []).map((account): Column<AccountSpendDay> => ({
      key: account.id,
      header: account.name,
      headerClassName: 'text-right',
      className: 'text-right tabular-nums',
      render: (row) => money(row.cost[account.id] ?? 0),
    })),
    // Shown only when something went unattributed: a benchmark, an eval, or
    // a run older than the account header. Kept apart so the total adds up.
    ...(hasUnattributed(rows)
      ? [
          {
            key: 'none',
            header: 'Sem conta',
            headerClassName: 'text-right',
            className: 'text-right tabular-nums text-text-weak',
            render: (row: AccountSpendDay) => money(row.cost[NO_ACCOUNT] ?? 0),
          },
        ]
      : []),
    {
      key: 'total',
      header: 'Total',
      headerClassName: 'text-right',
      className: 'text-right tabular-nums text-text-strong',
      render: (row) => money(row.total),
    },
  ]

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-tH3 text-text-strong">Custo por farmácia e por dia</h2>
      {!loading && data !== undefined && !data.spendAvailable ? (
        <p className="text-tSmall text-text-weak rounded-base border-stroke-weak bg-background-raised border p-4">
          Sem dados de custo por farmácia: o Langfuse não respondeu ou não está configurado.
        </p>
      ) : (
        <DataTable
          columns={columns}
          rows={spendByDay(rows)}
          rowKey={(row) => row.day}
          loading={loading}
          error={error}
          {...(onRetry === undefined ? {} : { onRetry })}
          pageSize={10}
          empty={`Nenhuma chamada nos últimos ${data?.days ?? ''} dias.`}
        />
      )}
    </section>
  )
}
